import { Autoplay, Navigation } from "../swiper/swiperConfig";

const FADE_DELAY = 600;
const FULL_LOADER_BACKGROUND = "full";
const LOADING_CLASS = "loading";
const UPDATE_LOADERS_DELAY = 600;

const recommendedSlider = "recommended-slider-block";

let loaders = [];
let viewedSlides = new Set();
let prevIndex = 0;
let isTransitioning = false;
let fadeInTimeout = null;
let countdownInterval = null;
let isBackwardLooped = false;

function getAdjacentSlides(swiper) {
  const active = swiper.slidesEl.querySelector(".swiper-slide-active");
  const next = swiper.slidesEl.querySelector(".swiper-slide-next");
  const prev = swiper.slidesEl.querySelector(".swiper-slide-prev");
  return [active, next, prev];
}

function addFadeInToSlides(...slides) {
  slides.forEach((slide) => {
    if (slide) {
      slide.classList.add("fade-in");
    }
  });
}

function startAutoplayCountdown(swiper) {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }

  countdownInterval = setInterval(() => {
    const timeLeft = swiper.autoplay.paused ? 0 : swiper.autoplay.timeLeft;

    if (timeLeft > 0 && timeLeft <= 200) {
      const [active, next, prev] = getAdjacentSlides(swiper);
      addFadeInToSlides(active, next, prev);
    }
  }, 50);
}

function removeFadeInFromAllSlides(swiper) {
  if (fadeInTimeout) {
    clearTimeout(fadeInTimeout);
  }

  fadeInTimeout = setTimeout(() => {
    swiper.slides.forEach((slide) => {
      if (slide.classList.contains("swiper-slide-active")) {
        slide.classList.remove("fade-in");
      }
    });
  }, 320);
}

/**
 * Обновляет классы загрузчиков на основе того, какие слайды были просмотрены.
 */
function updateLoaders() {
  loaders.forEach((loader) => {
    const index = parseInt(loader.getAttribute("data-index"), 10);
    loader.classList.toggle(LOADING_CLASS, viewedSlides.has(index));
  });
}

/**
 * Помечает загрузчик как просмотренный.
 * @param {number} index - Индекс загрузчика.
 * @param {HTMLElement} loaderEl - Элемент загрузчика для обновления.
 */
const markLoaderAsViewed = (index, loaderEl) => {
  loaderEl?.classList.add(FULL_LOADER_BACKGROUND);
  setTimeout(() => {
    viewedSlides.add(index);
  }, FADE_DELAY);
};

/**
 * Обработчик клика на загрузчике, который запускает переход к другому слайду.
 * @param {Object} swiper - Экземпляр слайдера.
 * @param {number} targetIndex - Индекс целевого слайда для перехода.
 */
function createLoaderClickHandler(swiper, targetIndex) {
  if (!isTransitioning) {
    if (isBackwardLooped) {
      viewedSlides.clear();
      isBackwardLooped = false;

      updateLoaders();
      swiper.slideToLoop(targetIndex);
    } else {
      swiper.slides.forEach((slide) => {
        if (slide.classList.contains("swiper-slide-active")) {
          const currentSlideIndex = Number(slide.getAttribute("data-swiper-slide-index"));

          const range = [
            Math.min(currentSlideIndex, targetIndex),
            Math.max(currentSlideIndex, targetIndex),
          ];

          for (let i = range[0]; i <= range[1]; i++) {
            if (!viewedSlides.has(i)) {
              const loaderEl = loaders[i - 1];
              markLoaderAsViewed(i, loaderEl);
            }
          }
        }
      });

      updateLoaders();
      swiper.slideToLoop(targetIndex);
    }
  }
}

/**
 * Обрабатывает изменение слайда.
 * @param {Object} swiper - Экземпляр слайдера Swiper.
 */
const handleSlideChange = (swiper) => {
  const currentIndex = swiper.realIndex;
  const totalSlides = swiper.slides.length;

  // Сброс при завершении цикла
  if (prevIndex === totalSlides - 1 && currentIndex === 0) {
    loaders.forEach(loader => loader.classList.remove(LOADING_CLASS, FULL_LOADER_BACKGROUND));
    viewedSlides.clear();
    updateLoaders();
  }

  if (prevIndex === 0 && currentIndex === totalSlides - 1) {
    isBackwardLooped = true;
    viewedSlides.clear();
    updateLoaders();
  }

  // Проверка изменения индекса
  if (currentIndex !== prevIndex) {
    const isForward = currentIndex > prevIndex;

    if (isForward) {
      if (!viewedSlides.has(currentIndex)) {
        viewedSlides.add(currentIndex);
        loaders[currentIndex - 1].classList.add(FULL_LOADER_BACKGROUND);
      }
    } else {
      const indexInArray = [...viewedSlides].indexOf(currentIndex);
      loaders[prevIndex]?.classList.remove(LOADING_CLASS);

      setTimeout(() => {
        loaders[currentIndex]?.classList.remove(LOADING_CLASS);
      }, FADE_DELAY);

      const maxViewedIndex = Math.max(0, ...viewedSlides);

      if (currentIndex < maxViewedIndex) {
        const filtered = [...viewedSlides].filter(
          index => index <= currentIndex,
        );
        viewedSlides = new Set(filtered);

        loaders
          .slice(currentIndex)
          .forEach(loader => loader.classList.remove(LOADING_CLASS, FULL_LOADER_BACKGROUND));
      }

      // Обновляем массив просмотренных
      if (indexInArray !== -1) {
        viewedSlides = new Set([...viewedSlides].slice(0, indexInArray + 1));
      } else {
        viewedSlides = new Set([currentIndex]);
      }
    }

    setTimeout(updateLoaders, UPDATE_LOADERS_DELAY);
    prevIndex = currentIndex;
  }
};

/**
 * Обрабатывает окончание перехода слайда после клика next/prev.
 * @param {Object} slides - Слайды Swiper.
 * @param {Object} fadeIn - true/false для добавления класса fadeIn.
 *
 */
function toggleClassAfterClickButton(slides, fadeIn) {
  slides.slides.forEach((slide) => {
    slide.classList.remove("fade-in");

    if (slide.classList.contains("swiper-slide-active")) {
      if (fadeIn) {
        slide.classList.add("fade-in");
      }
    }
  });
}

/**
 * Обрабатывает начало перехода слайда.
 * @param {Object} swiper - Экземпляр слайдера Swiper.
 */
const handleSlideStart = (swiper) => {
  isTransitioning = true;
  toggleClassAfterClickButton(swiper, true);
};

/**
 * Обрабатывает окончание перехода слайда.
 * @param {Object} swiper - Экземпляр слайдера Swiper.
 */
const handleSlideEnd = (swiper) => {
  isTransitioning = false;
  toggleClassAfterClickButton(swiper, false);
};

const configRecommendedSlider = {
  speed: 400,
  direction: "horizontal",
  allowTouchMove: false,
  autoplay: { delay: 7000 },
  loop: true,
  modules: [Autoplay, Navigation],
  watchSlidesProgress: true,
  breakpoints: {
    320: { slidesPerView: 1, spaceBetween: 34 },
  },
  on: {
    init() {
      loaders = Array.from(document.querySelectorAll(".progress-loader"));

      loaders.forEach((loader, index) => {
        loader.setAttribute("data-index", index);
        loader.addEventListener("click", () => createLoaderClickHandler(this, index));
      });

      if (this.params.autoplay.enabled) {
        const autoplayDelay = this.params.autoplay.delay;
        const containerLoader = this.el.querySelector(".progress-loaders");
        containerLoader.style.setProperty("--speed-main-slider", `${autoplayDelay / 1000}s`);
      }

      viewedSlides = new Set([this.realIndex]);
      updateLoaders();
      startAutoplayCountdown(this);
    },
    autoplayStart() {
      startAutoplayCountdown(this);
    },
    autoplayStop() {
      if (countdownInterval) clearInterval(countdownInterval);
    },
    slideChangeTransitionStart() {
      handleSlideStart(this);
    },
    beforeTransitionStart() {
      removeFadeInFromAllSlides(this);
    },
    slideChangeTransitionEnd() {
      handleSlideEnd(this);
    },
    slideChange() {
      handleSlideChange(this);
    },
  },
};

export { configRecommendedSlider, recommendedSlider };
